// API Configuration
// Use environment variables if available, fallback to localhost for development
export const BUILDER_API =
  process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const EXECUTOR_API =
  process.env.REACT_APP_EXECUTOR_URL || 'http://localhost:8001';

// ── Brain types ──────────────────────────────────────────────────────────────

export interface BrainFile {
  key: string;
  title: string;
  type: 'folder' | 'file';
  children?: BrainFile[];
}

export interface BrainFileContent {
  content: string;
  sha: string;
}

export interface BrainIndexingResult {
  chunks?: number;
  entities?: number;
  relationships?: number;
  errors: string[];
  // True when the indexing request timed out client-side before the executor
  // responded. The save/delete itself already succeeded — indexing is still
  // running in the background. Distinct from `errors`: this is not a failure.
  pending?: boolean;
  // True when the request did NOT index on purpose (`?index=false`): indexing
  // is a separate background request. Nothing to report yet.
  deferred?: boolean;
  // Server time (ISO) the indexing started: lets a caller that only got
  // `pending` ask later whether the index caught up.
  startedAt?: string;
  // `POST /brain/index`: the version it indexed (may be newer than the save that triggered it).
  sha?: string;
}

/** How the index currently holds one document (GET /brain/index-status). */
export interface BrainPathIndexState {
  path: string;
  indexed: boolean;
  chunks: number;
  indexed_at: string | null;
  /** SHA-256 of the version fully indexed (equals the `sha` a save returns); null while half done. */
  content_sha?: string | null;
}

export interface BrainSaveResponse {
  status: string;
  sha: string | null;
  indexing: BrainIndexingResult;
}

export interface BrainDeleteResponse {
  status: string;
  indexing: BrainIndexingResult;
}

export interface BrainDeleteFolderResponse {
  status: string;
  deleted: number;
  /** The documents removed from the repo: what still has to be de-indexed. */
  deletedPaths: string[];
  indexing: BrainIndexingResult;
}

// Node Types
export type NodeType =
  | 'extraction'
  | 'search'
  | 'validation'
  | 'review'
  | 'classification'
  | 'persistence'
  | 'custom';

export type ModelProvider = 'vertex_ai' | 'openai' | 'anthropic';

// Agent Configuration
export interface AgentConfig {
  model_name: string;
  model_provider: ModelProvider;
  temperature: number;
  recursion_limit: number;
  name: string;
  task: string;
  role: string;
  description: string;
  expected_output: string;
  update_kg?: boolean;
  system_prompt?: string; // Optional, generated by backend
}

// Node Configuration
export interface NodeConfig {
  type: NodeType;
  name: string;
  prompt: string;
  config: string;
}

// API Request Types
export interface BuilderConfigRequest {
  configurable: AgentConfig;
}

export interface NodeConfigRequest {
  agent_id: string;
  type: NodeType;
  name: string;
  prompt?: string;
  config?: Record<string, unknown>;
}

// API Response Types
export interface BuilderConfigResponse {
  status: string;
  message: string;
  agent_id: string;
  config: AgentConfig & { system_prompt: string };
}

export interface NodeDetail {
  id: string;
  type: string;
  name: string;
  prompt: string | null;
  config: Record<string, unknown>;
}

export interface NodeConfigResponse {
  status: string;
  message: string;
  node_id: string;
  agent_id: string;
  node: NodeDetail;
}

export interface ActivationResponse {
  status: string;
  message: string;
  agent_id: string;
  webhook_url: string;
  nodes_count: number;
}

export interface ErrorResponse {
  error?: string;
  detail?: string;
}

// ── Agent List Types ──
export interface AgentSummary {
  id: string;
  name: string;
  role: string;
  task: string;
  description: string;
  modelName: string;
  modelProvider: ModelProvider;
  temperature: number;
  recursionLimit: number;
  expectedOutput: string;
  systemPrompt: string;
  updateKg?: boolean;
  isMarketplace?: boolean;
  marketplaceKey?: string;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  cognitiveSpec?: Record<string, unknown> | null;
}

export interface AgentListResponse {
  status: string;
  message: string;
  agents: AgentSummary[];
}

// ── Agent Nodes Response ──
export interface BackendNode {
  id: string;
  agentId: string;
  type: string;
  name: string;
  prompt: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AgentNodesResponse {
  status: string;
  message: string;
  agent_id: string;
  nodes: BackendNode[];
}

// ── FlowDefinition Types (matches executor Pydantic schemas) ──

export interface EdgeCondition {
  field: string;
  operator: string;
  value: string;
}

export interface FDNodeDefinition {
  id: string;
  type: string;
  name: string;
  prompt?: string;
  config: Record<string, unknown>;
  inputs?: string[];
  outputs?: string[];
}

export interface FDEdgeDefinition {
  id: string;
  source: string;
  target: string;
  condition?: EdgeCondition | null;
}

export interface FlowDefinitionPayload {
  nodes: FDNodeDefinition[];
  edges: FDEdgeDefinition[];
}

export interface FlowSaveResponse {
  status: string;
  message: string;
  agent_id: string;
  version: number;
  flow_id: string;
}

export interface FlowGetResponse {
  status: string;
  message: string;
  agent_id: string;
  version: number;
  flow_definition: {
    flow_id: string;
    agent_id: string;
    version: number;
    name: string;
    description: string;
    nodes: FDNodeDefinition[];
    edges: FDEdgeDefinition[];
  };
}

// UI Types
export interface NodeTypeOption {
  value: NodeType;
  label: string;
}

export const NODE_TYPES: NodeTypeOption[] = [
  { value: 'extraction', label: 'Extraction' },
  { value: 'search', label: 'Search' },
  { value: 'validation', label: 'Validation' },
  { value: 'review', label: 'Review' },
  { value: 'classification', label: 'Classification' },
  { value: 'persistence', label: 'Persistence' },
  { value: 'custom', label: 'Custom' },
];

// ── Marketplace / Meeting Types ──

export interface SpeakerBlock {
  speaker: { name?: string };
  words: string;
}

export interface MeetingDoubtsRequest {
  body: {
    transcript: { speaker_blocks: SpeakerBlock[] };
    title?: string;
  };
}

// ── Ontology Types ──

export type OntologyPropertyType = 'string' | 'number' | 'boolean' | 'date';

export interface OntologyProperty {
  id: string;
  name: string;
  type: OntologyPropertyType;
}

export interface OntologyEntity {
  id: string;
  name: string;
  color: string;
  properties: OntologyProperty[];
}

export interface OntologyRelationship {
  id: string;
  name: string;
  sourceEntityId: string;
  targetEntityId: string;
}

export interface Ontology {
  id: string;
  agentId: string;
  name: string;
  entities: OntologyEntity[];
  relationships: OntologyRelationship[];
}

export type DoubtCategory =
  | 'timing'
  | 'taxes'
  | 'price'
  | 'energy'
  | 'sustainability'
  | 'logistics'
  | 'quality'
  | 'others';

export interface Doubt {
  description: string;   // English
  descripcion: string;   // Spanish
  category: DoubtCategory;
  type: 'doubt' | 'concern';
  tags: string[];
  level?: string;
}

export interface MeetingDoubtsResponse {
  doubts: Doubt[];
  bigquery_insert: null;
  bigquery_error: null;
}
